
import { computed, defineComponent, PropType, ref } from 'vue'
import { sendOffer } from '../api'
import { composeOfferMessage, sourceDetail } from '../helpers'
import { DayKey, HotWindow, HotWindowCandidate, HotWindowService } from '../types'
import { SendOption } from '../sendChannels'
import HwIcon from './HwIcon.vue'
import MoveFlow from './MoveFlow.vue'

/**
 * Предложение уходит клиенту по каскаду проекта (как ручная отправка из
 * списка задач). Канал выбирает каскад, поэтому выбора канала в модалке нет.
 */
export default defineComponent({
    name: 'HotWindowsOfferModal',
    components: { HwIcon, MoveFlow },
    props: {
        projectId: { type: String, required: true },
        candidate: { type: Object as PropType<HotWindowCandidate>, required: true },
        window: { type: Object as PropType<HotWindow>, required: true },
        service: { type: Object as PropType<HotWindowService>, required: true },
        start: { type: Number, required: true },
        dayKey: { type: String as PropType<DayKey>, required: true },
        date: { type: String, required: true },
        sendOptions: { type: Array as PropType<SendOption[]>, default: () => [] },
    },
    emits: ['close', 'sent', 'toast'],
    setup(props, { emit }) {
        // Компонент монтируется на каждое открытие — черновик всегда собран
        // под текущие окно, услугу и время.
        const text = ref(
            composeOfferMessage({
                candidate: props.candidate,
                window: props.window,
                service: props.service,
                start: props.start,
                dayKey: props.dayKey,
            })
        )

        const sending = ref(false)
        // По умолчанию — канал проекта по умолчанию, как было до выбора.
        const selectedKey = ref('default')
        const selectedSettings = () =>
            props.sendOptions.find(option => option.key === selectedKey.value)?.value ?? null

        async function send() {
            if (!text.value.trim()) {
                emit('toast', 'Добавьте текст сообщения')
                return
            }
            if (!props.candidate.phone) {
                emit('toast', 'У клиента нет телефона — отправить не получится')
                return
            }
            sending.value = true
            const { data, error } = await sendOffer({
                projectId: props.projectId,
                offer: {
                    clientId: props.candidate.clientId,
                    phone: props.candidate.phone,
                    clientName: props.candidate.name,
                    text: text.value,
                    sendingSettings: selectedSettings(),
                },
            })
            sending.value = false
            // Модалку не закрываем при ошибке: текст остаётся, можно поправить
            // и отправить ещё раз.
            if (error || !data) {
                emit('toast', 'Не удалось отправить предложение, попробуйте ещё раз')
                return
            }
            if (!data.sent) {
                emit('toast', data.reason || 'Сообщение не прошло проверку')
                return
            }
            emit('sent', { candidate: props.candidate })
        }

        return {
            text,
            title: computed(() =>
                props.candidate.source === 'transfer' ? 'Предложить перенос записи' : 'Предложить свободное окно'
            ),
            subtitle: computed(() => `${props.candidate.name || 'Клиент'} · ${sourceDetail(props.candidate, props.window.master)}`),
            send,
            sending,
            selectedKey,
            defaultOptions: computed(() => props.sendOptions.filter(option => option.value === null)),
            channelOptions: computed(() => props.sendOptions.filter(option => option.value?.type === 'channel')),
            cascadeOptions: computed(() => props.sendOptions.filter(option => option.value?.type === 'cascade')),
            selectedLabel: computed(
                () => props.sendOptions.find(option => option.key === selectedKey.value)?.label ?? ''
            ),
            sendLabel: computed(() =>
                props.candidate.source === 'transfer' ? 'Предложить перенос' : 'Отправить предложение'
            ),
        }
    },
})
